import type { AdminAudience } from './AdminPageHeader';

/** The admin area's pages, in navigation order. `group` is the left-nav heading. */

export type AdminPageKey =
  | 'overview'
  | 'people'
  | 'roles'
  | 'sign-in'
  | 'organisation'
  | 'organisations'
  | 'api-keys'
  | 'gis'
  | 'ai-assistants'
  | 'standing-orders'
  | 'entitlements';

export interface AdminPageDef {
  key: AdminPageKey;
  label: string;
  group: string | null;
  audience: AdminAudience;
}

export const ADMIN_PAGES: AdminPageDef[] = [
  { key: 'overview', label: 'Overview', group: null, audience: 'admins' },
  { key: 'people', label: 'People', group: 'People & access', audience: 'admins' },
  { key: 'roles', label: 'Roles & permissions', group: 'People & access', audience: 'admins' },
  { key: 'sign-in', label: 'Sign-in', group: 'People & access', audience: 'admins' },
  { key: 'organisation', label: 'Profile & branding', group: 'Organisation', audience: 'admins' },
  { key: 'organisations', label: 'Client organisations', group: 'Organisation', audience: 'partners' },
  { key: 'api-keys', label: 'API keys', group: 'Integrations', audience: 'admins' },
  { key: 'gis', label: 'GIS services', group: 'Integrations', audience: 'admins' },
  { key: 'ai-assistants', label: 'AI assistants', group: 'Integrations', audience: 'admins' },
  { key: 'standing-orders', label: 'Standing orders', group: 'Incident agent', audience: 'admins' },
  { key: 'entitlements', label: 'Entitlements', group: 'FiSci staff only', audience: 'fisci' },
];

export interface SettingsIndexEntry {
  title: string;
  page: AdminPageKey;
  /** Lower-case words people might type. */
  keywords: string;
}

/** What the "Find a setting" box searches. Titles are in the user's words. */
export const ADMIN_SETTINGS_INDEX: SettingsIndexEntry[] = [
  { title: 'Add a person', page: 'people', keywords: 'user invite create account new person add' },
  { title: 'Deactivate or delete a person', page: 'people', keywords: 'remove disable user leave deactivate delete' },
  { title: "Reset someone's password", page: 'people', keywords: 'password reset' },
  { title: 'Make someone an administrator', page: 'people', keywords: 'admin staff access level administrator' },
  { title: 'Notification subscriptions for a person', page: 'people', keywords: 'notifications sms email subscribe alerts' },
  { title: 'Create or edit a role', page: 'roles', keywords: 'group permission capability role' },
  { title: 'Sign-in method (Google, Entra ID, Okta, password)', page: 'sign-in', keywords: 'sso single sign-on oidc google workspace entra okta identity provider domain' },
  { title: 'Require single sign-on', page: 'sign-in', keywords: 'enforce sso require' },
  { title: 'Create accounts automatically', page: 'sign-in', keywords: 'jit provisioning automatic accounts default role' },
  { title: 'How often people sign in again', page: 'sign-in', keywords: 'session hours expiry sign in again' },
  { title: 'Your sign-in link / short name', page: 'sign-in', keywords: 'slug login url link short name' },
  { title: 'Organisation name', page: 'organisation', keywords: 'rename company tenant name' },
  { title: 'Logo in the top bar', page: 'organisation', keywords: 'branding logo image upload' },
  { title: 'Create a client organisation', page: 'organisations', keywords: 'tenant child partner organisation create' },
  { title: 'Who can work in a client organisation', page: 'organisations', keywords: 'access grant colleague enter switch' },
  { title: 'Create an API key', page: 'api-keys', keywords: 'token bearer client integration api key' },
  { title: 'Rotate or disable an API key', page: 'api-keys', keywords: 'rotate revoke token disable' },
  { title: 'WMS / WMTS / WCS endpoints', page: 'gis', keywords: 'arcgis qgis geoserver connect gis url wms wmts wcs' },
  { title: 'GIS credentials', page: 'gis', keywords: 'username password key geoserver credential' },
  { title: 'Published fires and how long they are kept', page: 'gis', keywords: 'retention publish layer remove days fires' },
  { title: 'Allow AI assistants', page: 'ai-assistants', keywords: 'mcp claude connect assistant allow' },
  { title: 'Disconnect an AI assistant', page: 'ai-assistants', keywords: 'mcp revoke connection disconnect' },
  { title: 'Incident agent standing orders', page: 'standing-orders', keywords: 'agent instructions ai monitoring brief orders' },
  { title: 'Is AI incident monitoring included?', page: 'standing-orders', keywords: 'plan entitlement agent included' },
  { title: 'Live detection feeds (hotspots, lightning, hazards)', page: 'entitlements', keywords: 'realtime satellite lightning hazard entitlement tenant capability feeds' },
  { title: 'Can create client organisations (entitlement)', page: 'entitlements', keywords: 'partner child tenants allow entitlement' },
];
